import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/common/Breadcrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customtable/CustomTable";
import Action from "../../components/common/Action";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCommunalGarden,
  getComunalGardenlist,
} from "../../redux/communalgarden/slice";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { Badge } from "../../components/micro";
import { SortDownArrow } from "../../assets/svg";

const CommunalGarden = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [gardenDelete, setGardenDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);

  // selector
  const { communalGardenList, communalGardenListLoading } = useSelector(
    (store) => ({
      communalGardenList:
        store?.communalGardenData?.getComunalGardenlistStatus?.data,
      communalGardenListLoading: store?.communalGardenData?.loading,
    })
  );

  useEffect(() => {
    dispatch(
      getComunalGardenlist({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  const handleDeleteCommunalGarden = () => {
    setIsLoading(true);
    if (!gardenDelete) return;
    dispatch(deleteCommunalGarden(gardenDelete)).then((res) => {
      setIsLoading(false);
      if (res.type === "deleteCommunalGarden/fulfilled") {
        dispatch(
          getComunalGardenlist({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
      setConfirmDelete(false);
    });
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 w-16 table-th">No.</th>
      <th className="w-40 table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("creatorName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Creator Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "creatorName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className=" table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("thoughts");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Thoughts</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "thoughts"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="font-Proxima w-28 text-sm font-normal py-4 px-4 text-mgrey text-start">
        Like Count
      </th>
      <th className="w-32 table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("status");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Status</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "status"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="w-20 table-th">Action</th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(communalGardenList?.data?.data || []).map((item, index) => (
        <tr key={item.id} className=" shadow-sm text-mgrey hover:bg-navTheme/5">
          <td className="px-5 py-3">
            {(pageCount - 1) * pageLimit + index + 1}
          </td>
          <td className="px-5 py-3">
            <div className="line-clamp-2" title={item?.creatorName || "-"}>
              {item?.creatorName || "-"}
            </div>
          </td>
          <td className="px-5 py-3 " title={item?.thoughts || "-"}>
            <div className="line-clamp-2">{item?.thoughts || "-"}</div>
          </td>
          <td className="px-5 py-3">{item?.likeCount}</td>
          <td className="table-col">
            <div className="flex justify-start">
              <Badge
                className={`uppercase ${
                  item?.approvalStatus === "approve"
                    ? "border-themeBtn text-themeBtn"
                    : item?.approvalStatus === "pending"
                    ? "border-lightyellow text-lightredtheme bg-lightyellow border "
                    : "text-white bg-red"
                }`}
                value={
                  item?.approvalStatus === "approve"
                    ? "Approved"
                    : item?.approvalStatus === "pending"
                    ? "Pending"
                    : "Rejected"
                }
              />
            </div>
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/add-edit-communal-garden", {
                        state: { garden: item, mode: "edit" },
                      })
                    }
                  >
                    View / Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setGardenDelete(item?._id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );
  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Communal Garden
          </h6>
        </div>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"Communal Garden"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase font-Proxima text-base w-40 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => {
              navigate("/add-edit-communal-garden");
            }}
            type="button"
          >
            + Add
          </button>
        </div>
        <div className="w-full bg-white shadow rounded-lg">
          {communalGardenListLoading ? (
            <RippleLoader />
          ) : (
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={communalGardenList?.data}
              loading={false}
              showPagination={true}
              disableNext={!!communalGardenList?.data?.length}
              disablePrev={!!communalGardenList?.data?.length}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={communalGardenList?.data?.pagination?.total || 0}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(
                communalGardenList?.data?.pagination?.total / pageLimit
              )}
            />
          )}
        </div>
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            isLoading={isLoading}
            confirmText={"Delete"}
            onClose={() => setConfirmDelete(false)}
            onConfirm={handleDeleteCommunalGarden}
          />
        </div>
      )}
    </>
  );
};

export default CommunalGarden;
